import { Gauge, ChartPieSlice, Lightning, Buildings, Lightbulb, Warning, ListChecks } from "@phosphor-icons/react";

function Card({ title, icon: Icon, accent = "amber", children }) {
  return (
    <div className={`rounded-sm border border-zinc-800 bg-zinc-900/40 p-5 hover:border-${accent}-500/30 transition-colors`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} weight="bold" className={`text-${accent}-400`} />
        <h3 className="font-heading text-sm font-bold text-zinc-100 tracking-tight">{title}</h3>
      </div>
      <div className="text-[12px] leading-relaxed text-zinc-400 space-y-2">{children}</div>
    </div>
  );
}

const FACTORS = [
  { label: "Payment history", weight: 35, color: "emerald", note: "Single biggest factor — one 30-day late payment can drop your score 60–110 points and stays for 7 years." },
  { label: "Credit utilization", weight: 30, color: "amber", note: "Balance ÷ limit, per card and overall. Keep under 30%, ideally under 10%. Pay down BEFORE the statement closes, not the due date." },
  { label: "Length of credit history", weight: 15, color: "blue", note: "Average age of all your accounts. Closing your oldest card hurts you. Don't do it." },
  { label: "Credit mix", weight: 10, color: "violet", note: "Having both revolving (cards) and installment (auto/mortgage/student) debt is a small plus. Don't take loans just for this." },
  { label: "New credit / inquiries", weight: 10, color: "red", note: "Each hard pull dings 5–10 points for ~12 months. Avoid opening 3+ accounts in a 6-month window." },
];

export default function CreditScoreBasics() {
  return (
    <div className="px-5 py-6 space-y-6 max-w-[1200px]" data-testid="credit-score-page">
      {/* Hero */}
      <div className="rounded-sm border border-blue-500/30 bg-gradient-to-br from-blue-500/10 via-zinc-900/40 to-zinc-950 p-6">
        <div className="flex items-center gap-3 mb-3">
          <Gauge size={28} weight="fill" className="text-blue-400" />
          <h2 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Credit Score Basics</h2>
        </div>
        <p className="text-[13px] text-zinc-400 leading-relaxed max-w-2xl">
          Your credit score is a 300–850 number that lenders use to decide what to charge you for borrowed money — but it's also
          increasingly used by <span className="text-blue-300">landlords, employers, and insurance companies</span>. A
          50-point swing can mean tens of thousands of dollars over the life of a mortgage.
        </p>
      </div>

      {/* Score ranges */}
      <Card title="The score ranges" icon={ChartPieSlice} accent="blue">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 my-2">
          {[
            { range: "300–579", label: "Poor", color: "red" },
            { range: "580–669", label: "Fair", color: "orange" },
            { range: "670–739", label: "Good", color: "yellow" },
            { range: "740–799", label: "Very Good", color: "emerald" },
            { range: "800–850", label: "Excellent", color: "blue" },
          ].map(b => (
            <div key={b.label} className={`p-3 rounded-sm border bg-${b.color}-500/5 border-${b.color}-500/30 text-center`}>
              <p className={`text-[10px] uppercase tracking-wider text-${b.color}-400 font-bold`}>{b.label}</p>
              <p className="font-data text-sm text-zinc-100 mt-1">{b.range}</p>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-zinc-500 italic mt-3">
          Above 740 is the sweet spot — lenders give you their best rates. Going from 720 to 800 doesn't help you much; going from 660 to 720 changes your life.
        </p>
      </Card>

      {/* Factors */}
      <Card title="What goes into the score" icon={ListChecks} accent="emerald">
        <p>FICO uses 5 factors, each with a different weight. Optimize the ones that move the needle:</p>
        <div className="space-y-2 my-3">
          {FACTORS.map(f => (
            <div key={f.label} className="flex items-start gap-3 p-3 rounded-sm bg-zinc-950/60 border border-zinc-800">
              <div className="shrink-0 w-12 text-right">
                <p className={`text-base font-data font-bold text-${f.color}-400`}>{f.weight}%</p>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-medium text-zinc-200">{f.label}</p>
                <p className="text-[11px] text-zinc-500 leading-relaxed mt-0.5">{f.note}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Raise it fast */}
      <Card title="How to raise your score quickly" icon={Lightning} accent="amber">
        <p>Most of these move your score within <strong className="text-zinc-200">30–60 days</strong> — credit bureaus update monthly when card issuers report:</p>
        <ol className="list-decimal pl-5 space-y-2 my-2">
          <li><strong className="text-zinc-200">Pay down credit-card balances to below 10% utilization.</strong> Easiest, fastest lever. A $5,000 limit means keeping the balance under $500 when the statement closes.</li>
          <li><strong className="text-zinc-200">Pay TWICE a month</strong> — once before the statement closes (lowers reported utilization), once before the due date (avoids interest). Same money, dramatically different reported number.</li>
          <li><strong className="text-zinc-200">Set every bill to autopay-minimum.</strong> Pay the full amount manually, but autopay guarantees you never miss a payment because life got busy.</li>
          <li><strong className="text-zinc-200">Become an authorized user</strong> on a parent's or spouse's old, well-managed card — instantly inherits their account age and history.</li>
          <li><strong className="text-zinc-200">Dispute errors.</strong> 1 in 5 reports has at least one error. Pull all 3 free at <span className="text-blue-300">annualcreditreport.com</span>; dispute anything wrong.</li>
          <li><strong className="text-zinc-200">Ask for a credit limit increase</strong> on existing cards (without a hard pull if possible). Same balance, bigger limit = lower utilization ratio.</li>
          <li><strong className="text-zinc-200">Don't close old cards</strong> — keeping them open with a $0 balance helps both length-of-history and utilization.</li>
        </ol>
        <p className="text-red-300/80 text-[11px] mt-3">
          <Warning size={11} weight="fill" className="inline mr-1 -mt-0.5 text-red-400" />
          <strong>Avoid:</strong> "credit repair" companies that charge to do what you can do yourself for free. Most are scams.
        </p>
      </Card>

      {/* Why it matters */}
      <Card title="Why it actually matters — beyond loans" icon={Buildings} accent="violet">
        <ul className="list-disc pl-5 space-y-1.5 my-2">
          <li><strong className="text-zinc-200">Mortgages.</strong> A 750 vs 650 score on a 30-year $400k mortgage is roughly <span className="text-emerald-300">$80,000–$120,000</span> in interest savings.</li>
          <li><strong className="text-zinc-200">Auto loans.</strong> Same vehicle, same loan term — the difference between 4% and 14% interest is thousands.</li>
          <li><strong className="text-zinc-200">Renting an apartment.</strong> Most landlords pull credit. Sub-650 = bigger deposit or denied entirely.</li>
          <li><strong className="text-zinc-200">Job applications.</strong> Many financial / government / clearance roles run a credit check. Bad credit can cost you the offer.</li>
          <li><strong className="text-zinc-200">Insurance premiums.</strong> Many states allow auto/home insurers to use credit-based scores — bad credit can raise premiums 20–50%.</li>
          <li><strong className="text-zinc-200">Utilities and cell-phone deposits.</strong> Sub-600 scores often pay $100–500 deposits to start service.</li>
        </ul>
      </Card>

      <Card title="Pro tips" icon={Lightbulb} accent="amber">
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong className="text-zinc-200">Check it monthly</strong> — most card issuers (Capital One, Discover, Chase, Amex) show your FICO score for free. CreditKarma shows VantageScore (similar but not identical).</li>
          <li><strong className="text-zinc-200">Don't obsess over 5-point swings</strong> — scores wobble naturally as balances and reporting dates shift.</li>
          <li><strong className="text-zinc-200">Time big applications.</strong> If you're buying a house in 6 months, don't open new cards or finance anything in the meantime.</li>
          <li><strong className="text-zinc-200">Freeze your credit</strong> at all 3 bureaus when not actively applying for anything — free, prevents identity theft, takes 5 minutes to thaw when needed.</li>
        </ul>
      </Card>
    </div>
  );
}
